const DEFAULT_MAX_DIMENSION = 1600;
const DEFAULT_QUALITY = 0.72;
const DEFAULT_TARGET_BYTES = 900 * 1024;

const readFileAsDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('No se pudo leer el archivo'));
    reader.readAsDataURL(file);
  });

const loadImage = (dataUrl: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('No se pudo procesar la imagen'));
    image.src = dataUrl;
  });

const canvasToBlob = (canvas: HTMLCanvasElement, mimeType: string, quality: number): Promise<Blob> =>
  new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('No se pudo generar el blob de imagen'));
        return;
      }
      resolve(blob);
    }, mimeType, quality);
  });

const getScaledSize = (width: number, height: number, maxDimension: number) => {
  const scale = Math.min(1, maxDimension / Math.max(width, height));
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale))
  };
};

const getOutputMimeType = (file: File) => {
  if (file.type === 'image/png') return 'image/png';
  if (file.type === 'image/webp') return 'image/webp';
  return 'image/jpeg';
};

export const optimizeImageForUpload = async (
  file: File,
  maxDimension = DEFAULT_MAX_DIMENSION,
  targetBytes = DEFAULT_TARGET_BYTES
): Promise<File> => {
  if (!file.type.startsWith('image/')) return file;
  if (file.size <= targetBytes) return file;

  try {
    const dataUrl = await readFileAsDataUrl(file);
    const image = await loadImage(dataUrl);
    const { width, height } = getScaledSize(image.naturalWidth, image.naturalHeight, maxDimension);

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext('2d');
    if (!context) return file;
    context.drawImage(image, 0, 0, width, height);

    const outputType = getOutputMimeType(file);
    let quality = DEFAULT_QUALITY;
    let blob = await canvasToBlob(canvas, outputType, quality);
    while (blob.size > targetBytes && quality > 0.45) {
      quality -= 0.08;
      blob = await canvasToBlob(canvas, outputType, quality);
    }

    if (blob.size >= file.size) return file;
    const extension = outputType === 'image/png' ? 'png' : outputType === 'image/webp' ? 'webp' : 'jpg';
    const fileName = file.name.includes('.') ? file.name.replace(/\.[^/.]+$/, `.${extension}`) : `${file.name}.${extension}`;
    return new File([blob], fileName, { type: outputType, lastModified: Date.now() });
  } catch {
    return file;
  }
};

export const fileToBase64WithoutPrefix = async (file: File): Promise<string> => {
  const dataUrl = await readFileAsDataUrl(file);
  const parts = dataUrl.split(',');
  return parts[1] ?? '';
};
